/**
 * GP HTL Revenue & Equity Tab
 * Shows revenue summary, equity milestones, and revenue by period chart
 */
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Verified as VerifiedIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  useGphtlRevenueSummary,
  useGphtlRevenueByPeriod,
  useGphtlEquityMilestones,
  useGphtlEquityLedger,
} from '../../../../../hooks/useGphtl';
import { GphtlEquityMilestone, GphtlRevenueFilters, GphtlEquityLedgerEntry } from '../../../../../types/gphtl.types';
import { MilestoneVerificationDialog } from '../components/MilestoneVerificationDialog';

// Format currency for display
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format currency for chart axis
const formatAxisCurrency = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// Format date for display
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Milestone progress component
interface MilestoneCardProps {
  milestone: GphtlEquityMilestone;
  onVerify?: (milestone: GphtlEquityMilestone) => void;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({ milestone, onVerify }) => {
  // Use actual backend field names
  const targetValue = milestone.revenue_threshold ?? 0;
  const equityPercentage = milestone.equity_percentage ?? 0;
  const status = milestone.status;
  const isAchieved = status === 'ACHIEVED';
  const isVerified = status === 'ACHIEVED' && milestone.verified_at != null;
  const isInProgress = status === 'IN_PROGRESS';
  // Check if milestone needs verification (achieved but not yet verified by admin)
  const needsVerification = isAchieved && !milestone.verified_at;

  // Progress: 100 if achieved/verified, 0 if pending (actual progress would need cumulative revenue from equity status)
  const progress = isAchieved || isVerified ? 100 : isInProgress ? 50 : 0;

  // Status chip label
  const getStatusLabel = () => {
    if (isVerified) return `Verified: ${equityPercentage}%`;
    if (isAchieved) return `Achieved: ${equityPercentage}%`;
    if (isInProgress) return 'In Progress';
    return 'Pending';
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {milestone.milestone_name} ({equityPercentage}%)
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {isVerified ? (
              <Chip
                icon={<VerifiedIcon />}
                label={getStatusLabel()}
                color="success"
                size="small"
              />
            ) : isAchieved ? (
              <Chip
                icon={<CheckIcon />}
                label={getStatusLabel()}
                color="warning"
                size="small"
              />
            ) : (
              <Chip
                icon={<PendingIcon />}
                label={getStatusLabel()}
                color="default"
                size="small"
              />
            )}
            {needsVerification && onVerify && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<VerifiedIcon />}
                onClick={() => onVerify(milestone)}
              >
                Verify
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: isVerified ? 'success.main' : isAchieved ? 'warning.main' : 'primary.main',
                },
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50 }}>
            {progress.toFixed(0)}%
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {milestone.description}
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {targetValue > 0 && (
            <Typography variant="caption" color="text.secondary">
              Target: {formatCurrency(targetValue)}
            </Typography>
          )}
          {milestone.time_requirement_months && (
            <Typography variant="caption" color="text.secondary">
              Time Req: {milestone.time_requirement_months} months
            </Typography>
          )}
          {milestone.achieved_date && (
            <Typography variant="caption" color="success.main">
              Achieved: {formatDate(milestone.achieved_date)}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Equity Ledger component
interface EquityLedgerDisplayProps {
  entries: GphtlEquityLedgerEntry[];
  currentBalance: number;
  isLoading: boolean;
}

const EquityLedgerDisplay: React.FC<EquityLedgerDisplayProps> = ({
  entries,
  currentBalance,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No equity transactions recorded yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Transaction History
        </Typography>
        <Chip
          label={`Current Balance: ${currentBalance.toFixed(2)}%`}
          color="primary"
          variant="outlined"
        />
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Milestone</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.ledger_id}>
                <TableCell>{formatDate(entry.effective_date)}</TableCell>
                <TableCell>
                  {entry.milestone_name ? (
                    <Chip
                      label={entry.milestone_name}
                      size="small"
                      color={entry.equity_amount >= 0 ? 'success' : 'error'}
                    />
                  ) : (
                    <Chip label="Adjustment" size="small" color="default" />
                  )}
                </TableCell>
                <TableCell>{entry.notes || '-'}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: entry.equity_amount >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'medium',
                  }}
                >
                  {entry.equity_amount >= 0 ? '+' : ''}
                  {entry.equity_amount.toFixed(2)}%
                </TableCell>
                <TableCell align="right">{entry.running_total.toFixed(2)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const GphtlRevenueTab: React.FC = () => {
  const [revenueFilters, setRevenueFilters] = useState<GphtlRevenueFilters>({
    groupBy: 'month',
  });
  const [equityTab, setEquityTab] = useState<'milestones' | 'ledger'>('milestones');
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<GphtlEquityMilestone | null>(null);

  const { data: summary, isLoading: summaryLoading, error: summaryError } = useGphtlRevenueSummary();
  const { data: periods, isLoading: periodsLoading } = useGphtlRevenueByPeriod(revenueFilters);
  const { data: equityData, isLoading: equityLoading, error: equityError, refetch: refetchMilestones } = useGphtlEquityMilestones();
  const { data: ledgerData, isLoading: ledgerLoading } = useGphtlEquityLedger();

  const handleVerifyClick = (milestone: GphtlEquityMilestone) => {
    setSelectedMilestone(milestone);
    setVerifyDialogOpen(true);
  };

  const handleVerifySuccess = () => {
    refetchMilestones();
  };

  if (summaryError || equityError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load revenue data: {summaryError?.message || equityError?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Revenue Summary Card */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !summary ? (
                <Typography color="text.secondary">No revenue data available.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Recognized</TableCell>
                        <TableCell align="right">Paid to GPHTL</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="medium">Gross Revenue</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(summary.totalGrossRevenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(summary.recognizedRevenue)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(summary.paidToGphtl)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="medium">GPHTL Share (80%)</Typography>
                        </TableCell>
                        <TableCell align="right" colSpan={3}>
                          {formatCurrency(summary.gphtlShare)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="medium">Barrett Royalty (20%)</Typography>
                        </TableCell>
                        <TableCell align="right" colSpan={3}>
                          {formatCurrency(summary.barrettRoyalty)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Equity Milestones Card */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Equity Tracking
                </Typography>
                {equityData && (
                  <Typography variant="body2" color="text.secondary">
                    Total Earned: {(equityData.totalEarned ?? 0).toFixed(2)}% of {equityData.totalPossible ?? 0}%
                  </Typography>
                )}
              </Box>

              <Tabs
                value={equityTab}
                onChange={(_, newValue) => setEquityTab(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab
                  icon={<TrendingUpIcon />}
                  iconPosition="start"
                  label="Milestones"
                  value="milestones"
                />
                <Tab
                  icon={<HistoryIcon />}
                  iconPosition="start"
                  label="Ledger"
                  value="ledger"
                />
              </Tabs>

              <Divider sx={{ mb: 2 }} />

              {equityTab === 'milestones' ? (
                equityLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : !equityData || equityData.milestones.length === 0 ? (
                  <Typography color="text.secondary">No equity milestones configured.</Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {equityData.milestones.map((milestone: GphtlEquityMilestone) => (
                      <MilestoneCard
                        key={milestone.milestone_id}
                        milestone={milestone}
                        onVerify={handleVerifyClick}
                      />
                    ))}
                  </Box>
                )
              ) : (
                <EquityLedgerDisplay
                  entries={ledgerData?.entries || []}
                  currentBalance={ledgerData?.currentBalance || 0}
                  isLoading={ledgerLoading}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue by Period Chart */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Revenue by Period
                </Typography>
                <Stack direction="row" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={revenueFilters.groupBy || 'month'}
                      label="Group By"
                      onChange={(e) =>
                        setRevenueFilters((prev) => ({
                          ...prev,
                          groupBy: e.target.value as 'month' | 'quarter' | 'year',
                        }))
                      }
                    >
                      <MenuItem value="month">Monthly</MenuItem>
                      <MenuItem value="quarter">Quarterly</MenuItem>
                      <MenuItem value="year">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={revenueFilters.dateFrom || ''}
                    onChange={(e) =>
                      setRevenueFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={revenueFilters.dateTo || ''}
                    onChange={(e) =>
                      setRevenueFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {periodsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !periods || periods.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No revenue data available for the selected period.
                </Typography>
              ) : (
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={periods}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis tickFormatter={formatAxisCurrency} />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(label) => `Period: ${label}`}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="grossRevenue"
                        name="Gross Revenue"
                        stackId="1"
                        stroke="#1976d2"
                        fill="#1976d2"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="gphtlShare"
                        name="GPHTL Share (80%)"
                        stackId="2"
                        stroke="#9c27b0"
                        fill="#9c27b0"
                        fillOpacity={0.3}
                      />
                      <Area
                        type="monotone"
                        dataKey="barrettRoyalty"
                        name="Barrett Royalty (20%)"
                        stackId="3"
                        stroke="#2e7d32"
                        fill="#2e7d32"
                        fillOpacity={0.3}
                      />
                      {/* Performance Milestone Reference Lines */}
                      <ReferenceLine
                        y={3000000}
                        stroke="#ff9800"
                        strokeDasharray="5 5"
                        label={{ value: '$3M', position: 'right', fill: '#ff9800', fontSize: 11 }}
                      />
                      <ReferenceLine
                        y={5000000}
                        stroke="#ed6c02"
                        strokeDasharray="5 5"
                        label={{ value: '$5M', position: 'right', fill: '#ed6c02', fontSize: 11 }}
                      />
                      <ReferenceLine
                        y={8000000}
                        stroke="#d32f2f"
                        strokeDasharray="5 5"
                        label={{ value: '$8M', position: 'right', fill: '#d32f2f', fontSize: 11 }}
                      />
                      <ReferenceLine
                        y={10000000}
                        stroke="#9c27b0"
                        strokeDasharray="5 5"
                        label={{ value: '$10M', position: 'right', fill: '#9c27b0', fontSize: 11 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Milestone Verification Dialog */}
      <MilestoneVerificationDialog
        open={verifyDialogOpen}
        onClose={() => setVerifyDialogOpen(false)}
        milestone={selectedMilestone}
        onSuccess={handleVerifySuccess}
      />
    </Box>
  );
};

export default GphtlRevenueTab;
